import { type Context, context, type Span, SpanStatusCode, trace } from '@repo/telemetry/api';

const TRACER_NAME = 'qontojs-sca';

export const SCA_FLOW_SPAN = 'sca.flow';
export const SCA_PROMPT_SPAN = 'sca.prompt';
export const SCA_RETRY_SPAN = 'sca.retry';

/**
 * A challenge with no successful replay within this budget is reported as abandoned.
 * The clock starts at the confirmed SCA 428, so it covers prompt time too — a user
 * who never finishes the challenge is as stuck as one whose replay never lands.
 */
export const SCA_STUCK_TIMEOUT_MS = 180_000;

export type ScaFlowOutcome =
  | 'succeeded'
  | 'cancelled'
  | 'prompt_failed'
  | 'retry_failed'
  | 'abandoned';

export type ScaPromptOutcome = 'succeeded' | 'cancelled' | 'failed';

export type ScaRetryOutcome = 'succeeded' | 'failed';

export interface ScaRequestAttributes {
  method?: string;
  path?: string;
  status?: number;
}

/**
 * Path only: query strings carry ids and filters we must not ship to the trace backend.
 * Relative or malformed urls keep their pathname rather than dropping the attribute.
 */
export function toSafePath(url: string | undefined): string | undefined {
  if (!url) return undefined;

  try {
    return new URL(url).pathname;
  } catch {
    return url.split('?')[0];
  }
}

function setRequestAttributes(span: Span, request: ScaRequestAttributes | undefined): void {
  if (!request) return;

  if (request.method) {
    span.setAttribute('http.request.method', request.method);
  }
  if (request.path) {
    span.setAttribute('url.path', request.path);
  }
  if (request.status !== undefined) {
    span.setAttribute('http.response.status_code', request.status);
  }
}

function tracer(): ReturnType<typeof trace.getTracer> {
  return trace.getTracer(TRACER_NAME);
}

export interface ScaRetryTrace {
  /** Runs the replay under the retry span so its HTTP span joins the flow trace. */
  run: <T>(fn: () => T) => T;
  succeeded: (status?: number) => void;
  failed: (status?: number) => void;
}

export interface ScaFlowTrace {
  /** Parent context for the prompt span, which is opened in another module. */
  readonly promptContext: Context;
  promptCompleted: () => void;
  startRetry: (request?: ScaRequestAttributes) => ScaRetryTrace;
  succeeded: (status?: number) => void;
  cancelled: () => void;
  promptFailed: (error?: unknown) => void;
  retryFailed: (status?: number, error?: unknown) => void;
}

export interface StartScaFlowTraceParams {
  errorType: string;
  request?: ScaRequestAttributes;
  /** Captured by the middleware before its first await. */
  parentContext?: Context;
  /** Overridable so tests can drive the deadline without waiting three minutes. */
  timeoutMs?: number;
}

/**
 * Owns the whole challenge: the root span, its 3-minute deadline, and the retry child.
 * The timeout lives here rather than in `promptSca` so prompt teardown and navigation
 * stay independent of telemetry.
 */
export function startScaFlowTrace({
  errorType,
  request,
  parentContext,
  timeoutMs = SCA_STUCK_TIMEOUT_MS,
}: StartScaFlowTraceParams): ScaFlowTrace {
  const parent = parentContext ?? context.active();
  const span = tracer().startSpan(SCA_FLOW_SPAN, undefined, parent);
  span.setAttribute('sca.error_type', errorType);
  setRequestAttributes(span, request);
  span.addEvent('sca.428_received');

  const startedAt = Date.now();
  const promptContext = trace.setSpan(parent, span);

  let settled = false;
  let timer: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
    timer = undefined;
    end('abandoned', () => {
      span.addEvent('sca.abandoned');
      span.setStatus({ code: SpanStatusCode.ERROR, message: 'sca.abandoned' });
    });
  }, timeoutMs);

  function clearDeadline(): void {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  }

  function end(outcome: ScaFlowOutcome, decorate?: () => void): void {
    if (settled) return;
    settled = true;
    clearDeadline();

    span.setAttribute('sca.outcome', outcome);
    span.setAttribute('sca.duration_ms', Date.now() - startedAt);
    decorate?.();
    span.end();
  }

  return {
    promptContext,

    promptCompleted() {
      if (settled) return;
      span.addEvent('sca.prompt_completed');
    },

    startRetry(retryRequest) {
      const retrySpan = tracer().startSpan(SCA_RETRY_SPAN, undefined, promptContext);
      retrySpan.setAttribute('sca.error_type', errorType);
      // The header name and token are credentials; only record that one was applied.
      retrySpan.setAttribute('sca.header_applied', true);
      setRequestAttributes(retrySpan, retryRequest);

      if (!settled) {
        span.addEvent('sca.retry_started');
      }

      let retrySettled = false;
      const endRetry = (outcome: ScaRetryOutcome, status?: number): void => {
        if (retrySettled) return;
        retrySettled = true;

        retrySpan.setAttribute('sca.outcome', outcome);
        if (status !== undefined) {
          retrySpan.setAttribute('sca.retry.response.status_code', status);
        }
        if (outcome === 'failed') {
          retrySpan.setStatus({ code: SpanStatusCode.ERROR });
        }
        retrySpan.end();
      };

      const retryContext = trace.setSpan(promptContext, retrySpan);

      return {
        run: fn => context.with(retryContext, fn),
        succeeded: status => endRetry('succeeded', status),
        failed: status => endRetry('failed', status),
      };
    },

    succeeded(status) {
      // A replay that lands after the deadline must not rewrite an exported abandoned span.
      if (settled) {
        tracer()
          .startSpan(SCA_RETRY_SPAN, undefined, promptContext)
          .addEvent('sca.retry_late_success')
          .end();
        return;
      }

      end('succeeded', () => {
        span.addEvent('sca.retry_succeeded');
        if (status !== undefined) {
          span.setAttribute('sca.retry.response.status_code', status);
        }
        span.setStatus({ code: SpanStatusCode.OK });
      });
    },

    cancelled() {
      end('cancelled', () => {
        span.addEvent('sca.prompt_cancelled');
        span.setStatus({ code: SpanStatusCode.OK });
      });
    },

    promptFailed(error) {
      end('prompt_failed', () => {
        span.addEvent('sca.prompt_failed');
        if (error instanceof Error) {
          span.recordException(error);
        }
        span.setStatus({ code: SpanStatusCode.ERROR, message: 'sca.prompt_failed' });
      });
    },

    retryFailed(status, error) {
      end('retry_failed', () => {
        span.addEvent('sca.retry_failed');
        if (status !== undefined) {
          span.setAttribute('sca.retry.response.status_code', status);
        }
        if (error instanceof Error) {
          span.recordException(error);
        }
        span.setStatus({ code: SpanStatusCode.ERROR, message: 'sca.retry_failed' });
      });
    },
  };
}

let requestContext: Context | undefined;

/** Scope for requests fired from the prompt UI, which renders in its own React root. */
export function setScaRequestContext(ctx: Context | undefined): void {
  requestContext = ctx;
}

/** Runs `fn` under the prompt span so the fetch instrumentation parents its HTTP span there. */
export function withScaRequestContext<T>(fn: () => T): T {
  return requestContext ? context.with(requestContext, fn) : fn();
}

export interface ScaPromptTrace {
  readonly context: Context;
  succeeded: () => void;
  cancelled: () => void;
  failed: (error?: unknown) => void;
}

/**
 * Child of the flow span when the middleware supplies its context; a standalone span
 * otherwise, so direct `promptSca` callers still report prompt outcomes.
 */
export function startScaPromptTrace({
  errorType,
  parentContext,
}: {
  errorType: string;
  parentContext?: Context;
}): ScaPromptTrace {
  const span = tracer().startSpan(SCA_PROMPT_SPAN, undefined, parentContext);
  span.setAttribute('sca.error_type', errorType);
  span.addEvent('sca.prompt_opened');
  const promptContext = trace.setSpan(parentContext ?? context.active(), span);

  const startedAt = Date.now();
  let settled = false;

  function end(outcome: ScaPromptOutcome, decorate?: () => void): void {
    if (settled) return;
    settled = true;

    span.setAttribute('sca.outcome', outcome);
    span.setAttribute('sca.duration_ms', Date.now() - startedAt);
    decorate?.();
    span.end();
  }

  return {
    context: promptContext,
    succeeded() {
      end('succeeded', () => {
        span.addEvent('sca.prompt_succeeded');
        span.setStatus({ code: SpanStatusCode.OK });
      });
    },

    cancelled() {
      end('cancelled', () => {
        span.addEvent('sca.prompt_cancelled');
        span.setStatus({ code: SpanStatusCode.OK });
      });
    },

    failed(error) {
      end('failed', () => {
        span.addEvent('sca.prompt_failed');
        if (error instanceof Error) {
          span.recordException(error);
        }
        span.setStatus({ code: SpanStatusCode.ERROR });
      });
    },
  };
}
